import { Logger } from '../../logger/interface/Logger';
import { Scanner } from '../interface/Scanner';
import { ScannerConnectionException } from '../interface/Exceptions/ScannerConnectionException';
import { ScannerOperationException } from '../interface/Exceptions/ScannerOperationException';
import { ErrorCode as ScannerConnectionErrorCode } from '../interface/Exceptions/ScannerConnectionException';
import { ErrorCode as ScannerOperationErrorCode } from '../interface/Exceptions/ScannerOperationException';

export class ScannerYoko extends EventTarget implements Scanner {
  //#region Constants
  private readonly VID = 0xe851;
  private readonly PID = 0x1002;
  private readonly SERIAL_PORT_BAUD_RATE = 9600;
  private readonly SERIAL_OPTIONS: SerialOptions = {
    baudRate: this.SERIAL_PORT_BAUD_RATE,
  };
  private isScannerConnected: boolean = false;
  private readonly logger: Logger;
  private readonly startDecoding: Uint8Array = new Uint8Array([
    0x57, 0x00, 0x18, 0x00, 0x55, 0x00,
  ]);
  private readonly triggerAnswer: Uint8Array = new Uint8Array([
    0x31, 0x00, 0x00, 0x00, 0x55, 0x00,
  ]);
  private readonly stopDecoding: Uint8Array = new Uint8Array([
    0x57, 0x00, 0x19, 0x00, 0x55, 0x00,
  ]);
  //#region Properties
  public name: string = '';
  private port: SerialPort | null;
  private reader: ReadableStreamDefaultReader<any> | null = null;
  private isSerialConnected: boolean = false;
  private isScannerConnecting: boolean = false;
  private desiredStateConnected: boolean = false;
  constructor(logger: Logger) {
    super();
    this.logger = logger;
    this.port = null;
    navigator.serial?.addEventListener('connect', (event) => {
      const port = event.target as SerialPort;
      if (this.validateVIDandPID(port)) {
        console.log('conected port event!', port);
        this.isSerialConnected = true;
        if (this.desiredStateConnected) {
          this.connectAsync().catch((error) => {
            console.error(error);
          });
        }
      }
    });
    navigator.serial.addEventListener('disconnect', (event) => {
      const port = event.target as SerialPort;
      if (this.validateVIDandPID(port)) {
        console.log('disconected port event!', port);
        this.isSerialConnected = false;
        this.onDisconnected();
      }
    });
  }

  connect(): void {
    this.isScannerConnected = true;
  }

  public async connectAsync(): Promise<void> {
    this.logger.info(this.name);
    this.isScannerConnecting = true;
    this.desiredStateConnected = true;
    try {
      await this.disconnectAsync();
    } catch (error) {
      console.log(error);
    }

    await this.setPortAsync().catch((error) => {
      console.log(error);
      this.dispatchEvent(new Event('authorization'));
    });
    if (this.port === null) {
      this.isScannerConnected = false;
      return Promise.reject(
        new ScannerConnectionException(
          ScannerConnectionErrorCode.CONNECTION,
          this.constructor.name
        )
      );
    }
    try {
      await this.port
        .open(this.SERIAL_OPTIONS)
        .then((value) => {
          this.isScannerConnected = true;
          this.logger.info(`${this.constructor.name} port opened.`);
          this.logger.info(value);
          this.onConnected();
        })
        .catch((error) => {
          this.logger.error(error);
        });
      this.isScannerConnecting = false;
      while (this.port && this.port.readable) {
        this.reader = this.port.readable.getReader();
        try {
          while (true) {
            const { value } = await this.reader.read();
            const str = new TextDecoder().decode(value);
            if (str.length > 6) {
              const scannedEvent = new CustomEvent('scan', { detail: str });
              this.dispatchEvent(scannedEvent);
            }
          }
        } catch (error) {
          this.logger.error(error);
        } finally {
          this.logger.info('release port');
          try {
            this.reader.releaseLock();
            this.port.close().catch((error) => console.error(error));
          } catch (error) {
            console.log(error);
          }
          break;
        }
      }
    } catch (error) {
      this.logger.error(error);
      if (error instanceof ScannerConnectionException) {
        if (error.code === ScannerConnectionErrorCode.DETECTION) {
          const authEvent = new Event('authentication');
          this.dispatchEvent(authEvent);
        }
      }
    }
    this.isSerialConnected = false;
    this.isScannerConnecting = false;
    this.onDisconnected();
    return Promise.reject(
      new ScannerConnectionException(
        ScannerConnectionErrorCode.CONNECTION,
        this.constructor.name
      )
    );
  }

  public async disconnectAsync(): Promise<void> {
    try {
      this.reader?.releaseLock();
      if (this.reader && this.port?.readable?.locked) {
        await this.reader.cancel();
        this.reader.releaseLock();
      }
      if (this.port?.writable?.locked) {
        await this.port?.writable.getWriter().abort();
        this.port?.writable.getWriter().releaseLock();
      }
      this.reader = null;
    } catch (ex) {
      console.error(ex);
    }
    try {
      await this.port?.close().catch((error) => console.error(error));
      this.port = null;
    } catch (error) {
      console.error(error);
    }
    return Promise.resolve();
  }

  disconnect(): void {
    this.isScannerConnected = false;
  }

  isConfigured(): boolean {
    return true;
  }

  pullTrigger() {
    // Implement the logic for pulling the trigger.
  }

  releaseTrigger() {
    // Implement the logic for releasing the trigger.
  }

  async pullTriggerAsync() {
    try {
      if (this.port !== null && this.isScannerConnected) {
        const writer = this.port.writable?.getWriter();
        if (writer) {
          await writer
            .write(this.startDecoding)
            .catch((error) => console.error(error));
          writer.releaseLock();
        }
      }
      return Promise.resolve();
    } catch (error) {
      console.error(error);
    }
    return Promise.reject(
      new ScannerOperationException(ScannerOperationErrorCode.PULLTRIGGER)
    );
  }

  async releaseTriggerAsync() {
    try {
      console.log('release trigger async');
      if (this.port !== null && this.isScannerConnected) {
        const writer = this.port.writable?.getWriter();
        if (writer) {
          await writer.write(this.stopDecoding);
          writer.releaseLock();
        }
      }

      return Promise.resolve();
    } catch (error) {
      console.error(error);
    }
    return Promise.reject(
      new ScannerOperationException(ScannerOperationErrorCode.RELEASETRIGGER)
    );
  }

  public isConnected(): boolean {
    return this.isScannerConnected;
  }

  public addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions | undefined
  ): void {
    super.addEventListener(type, callback, options);
  }
  public removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions | undefined
  ): void {
    super.removeEventListener(type, callback, options);
  }
  public dispatchEvent(event: Event): boolean {
    return super.dispatchEvent(event);
  }
  public async authorizeAsync(): Promise<void> {
    await this.requestPortsAsync().catch((error) => console.error(error));
    return await this.connectAsync().catch((error) => console.error(error));
  }
  private onConnected() {
    const connectedEvent = new Event('connection');
    this.dispatchEvent(connectedEvent);
  }
  private onDisconnected() {
    const disconnectedEvent = new Event('disconnection');
    this.dispatchEvent(disconnectedEvent);
  }
  private async setPortAsync(): Promise<void> {
    try {
      const ports = await this.getPortsAsync();
      if (ports !== null) {
        this.port = ports[0];
      }
      if (this.port) {
        console.log(
          `set ${this.constructor.name} port : ${
            this.port.getInfo().usbProductId
          }`
        );
        return Promise.resolve();
      }
    } catch (exception) {
      this.logger.error(exception);
    }
    return Promise.reject(
      new ScannerConnectionException(
        ScannerConnectionErrorCode.DETECTION,
        this.constructor.name
      )
    );
  }
  private async getPortsAsync(): Promise<SerialPort[] | null> {
    try {
      let connectedPorts: SerialPort[] = [];
      await navigator.serial
        .getPorts()
        .then((ports) => {
          ports.forEach(async (port) => {
            if (this.validateVIDandPID(port)) {
              connectedPorts.push(port);
            }
          });
        })
        .catch((error) => {
          this.logger.error(error);
        });
      if (connectedPorts.length > 0) {
        return connectedPorts;
      }
    } catch (exception) {
      this.logger.error(exception);
    }
    return null;
  }
  private validateVIDandPID(port: SerialPort): boolean {
    const { usbProductId, usbVendorId } = port.getInfo();
    const intVID = parseInt(this.VID.toString(), 10);
    const intPID = parseInt(this.PID.toString(), 10);
    if (usbProductId === intPID && usbVendorId === intVID) {
      return true;
    }
    return false;
  }
  private async requestPortsAsync(): Promise<void> {
    try {
      const filters = [
        {
          usbVendorId: this.VID,
          usbProductId: this.PID,
        },
      ];
      await navigator.serial.requestPort({ filters });
    } catch (error) {
      this.logger.error(error);
    }
    return;
  }
}
